import { Box } from '@mui/material'
import type { ReactNode } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Drawer } from '../Drawer'
import type { NavigationItem } from '../navigation.types'
import { NavigationRail, type NavigationRailProps } from '../NavigationRail'

export type NavigationRailShellProps = Omit<NavigationRailProps, 'activeItemId' | 'onItemSelect'> & {
  children: ReactNode
  drawerWidth?: number | string
  initialOpenItemId?: string | null
  onItemSelect?: (id: string | null) => void
}

const overlayCloseDelay = 200

export function NavigationRailShell({
  ariaLabel = 'Desktop app navigation',
  bottomItems = [],
  children,
  drawerWidth = 280,
  initialOpenItemId = null,
  logo,
  onItemSelect,
  topItems,
  width = 56,
}: NavigationRailShellProps) {
  const [drawerItemId, setDrawerItemId] = useState<string | null>(initialOpenItemId)
  const [overlayRenderItemId, setOverlayRenderItemId] = useState<string | null>(null)
  const closeTimerRef = useRef<number | null>(null)
  const openFrameRef = useRef<number | null>(null)

  const allItems = useMemo(() => [...topItems, ...bottomItems], [bottomItems, topItems])
  const activeDrawerItem = allItems.find(item => item.id === drawerItemId) ?? null
  const activeDockedItem = activeDrawerItem && !activeDrawerItem.drawerOverlay ? activeDrawerItem : null
  const activeOverlayItem = activeDrawerItem?.drawerOverlay ? activeDrawerItem : null
  const overlayDrawerItem = allItems.find(item => item.id === overlayRenderItemId) ?? null
  const resolveDrawerWidth = (item: NavigationItem | null) => item?.drawerWidth ?? drawerWidth

  const clearAnimationRefs = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }

    if (openFrameRef.current) {
      window.cancelAnimationFrame(openFrameRef.current)
      openFrameRef.current = null
    }
  }

  useEffect(() => clearAnimationRefs, [])

  const scheduleOverlayUnmount = (closingId: string | null) => {
    if (!closingId) {
      setOverlayRenderItemId(null)
      return
    }

    closeTimerRef.current = window.setTimeout(() => {
      setOverlayRenderItemId(current => (current === closingId ? null : current))
      closeTimerRef.current = null
    }, overlayCloseDelay)
  }

  const closeOverlayDrawer = () => {
    const closingId = overlayRenderItemId ?? activeOverlayItem?.id ?? null
    clearAnimationRefs()
    setDrawerItemId(null)
    onItemSelect?.(null)
    scheduleOverlayUnmount(closingId)
  }

  const openOverlayDrawer = (item: NavigationItem) => {
    clearAnimationRefs()

    if (activeOverlayItem) {
      setOverlayRenderItemId(item.id)
      setDrawerItemId(item.id)
      onItemSelect?.(item.id)
      return
    }

    setDrawerItemId(null)
    setOverlayRenderItemId(item.id)
    onItemSelect?.(item.id)

    openFrameRef.current = window.requestAnimationFrame(() => {
      setDrawerItemId(item.id)
      openFrameRef.current = null
    })
  }

  const handleItemSelect = (id: string) => {
    const item = allItems.find(entry => entry.id === id)

    if (!item?.drawerContent) {
      if (overlayRenderItemId) {
        closeOverlayDrawer()
        return
      }

      clearAnimationRefs()
      setDrawerItemId(null)
      onItemSelect?.(null)
      return
    }

    if (item.drawerOverlay) {
      if (drawerItemId === id || overlayRenderItemId === id) {
        closeOverlayDrawer()
        return
      }

      openOverlayDrawer(item)
      return
    }

    if (overlayRenderItemId) {
      const closingId = overlayRenderItemId
      clearAnimationRefs()
      setDrawerItemId(id)
      onItemSelect?.(id)
      scheduleOverlayUnmount(closingId)
      return
    }

    setDrawerItemId(current => {
      const nextId = current === id ? null : id
      onItemSelect?.(nextId)
      return nextId
    })
  }

  const closeDrawer = () => {
    if (overlayRenderItemId) {
      closeOverlayDrawer()
      return
    }

    clearAnimationRefs()
    setDrawerItemId(null)
    onItemSelect?.(null)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <NavigationRail
        activeItemId={drawerItemId}
        ariaLabel={ariaLabel}
        bottomItems={bottomItems}
        logo={logo}
        onItemSelect={handleItemSelect}
        topItems={topItems}
        width={width}
      />

      <Drawer
        anchor="left"
        closable
        header={activeDockedItem?.label ?? null}
        hideHeader={activeDockedItem?.hideHeader}
        onClose={closeDrawer}
        open={Boolean(activeDockedItem)}
        overlay={false}
        width={resolveDrawerWidth(activeDockedItem)}
      >
        {activeDockedItem?.drawerContent ?? null}
      </Drawer>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          position: 'relative',
        }}
      >
        {overlayDrawerItem ? (
          <Box
            sx={{
              bottom: 0,
              left: 0,
              position: 'absolute',
              top: 0,
              width: 0,
              zIndex: 2,
            }}
          >
            <Drawer
              anchor="left"
              closable
              header={overlayDrawerItem.label}
              hideHeader={overlayDrawerItem.hideHeader}
              onClose={closeDrawer}
              open={Boolean(activeOverlayItem)}
              overlay={false}
              width={resolveDrawerWidth(overlayDrawerItem)}
            >
              {overlayDrawerItem.drawerContent ?? null}
            </Drawer>
          </Box>
        ) : null}
        {children}
      </Box>
    </Box>
  )
}
